import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, Table } from "react-bootstrap";

import { request } from "hooks/useOkoApi/useOkoApi";

const PAGE_SIZE = 50;

/**
 * Owner-scoped student picker for ad-hoc grouping. Lists a school's assessed
 * students (email + assessment count) with search + pagination, and lets the
 * operator check the ones to include in a hand-assembled class. Selection is
 * lifted to the parent via onChange(selectedIds). Reuses the Ednition students
 * read endpoint (GET /integrations/ednition/students).
 */
export const StudentPicker = ({ schoolId, selectedIds, onChange }) => {
  const [students, setStudents] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [error, setError] = useState(null);

  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);

  useEffect(() => {
    setOffset(0);
  }, [schoolId, search]);

  const load = useCallback(async () => {
    if (!schoolId) {
      setStudents([]);
      setTotal(0);
      return;
    }
    const query = new URLSearchParams({ school_id: schoolId });
    query.set("limit", String(PAGE_SIZE));
    query.set("offset", String(offset));
    if (search.trim()) query.set("q", search.trim());
    try {
      const data = await request(
        `integrations/ednition/students?${query.toString()}`,
        { method: "GET" }
      );
      setStudents(data?.students || []);
      setTotal(data?.page?.total ?? 0);
    } catch (e) {
      setError(e?.detail || "Failed to load students");
    }
  }, [schoolId, offset, search]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next));
  };

  const togglePage = (checked) => {
    const next = new Set(selected);
    students.forEach((s) => {
      if (checked) next.add(s.id);
      else next.delete(s.id);
    });
    onChange(Array.from(next));
  };

  const rangeStart = total === 0 ? 0 : offset + 1;
  const rangeEnd = Math.min(offset + PAGE_SIZE, total);
  const allPageChecked =
    students.length > 0 && students.every((s) => selected.has(s.id));

  if (!schoolId) return null;

  return (
    <div className="student-picker">
      {error && <p className="text-danger">{error}</p>}
      <Form.Group controlId="studentSearch" style={{ marginTop: "12px" }}>
        <Form.Label>Search students</Form.Label>
        <Form.Control
          type="text"
          data-testid="student-search"
          placeholder="Filter by email, first name, or last name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Form.Group>

      <p className="text-muted" style={{ marginBottom: "4px" }}>
        {selected.size} selected
      </p>

      <Table striped bordered hover size="sm">
        <thead>
          <tr>
            <th>
              <Form.Check
                type="checkbox"
                aria-label="Select page"
                data-testid="select-page"
                checked={allPageChecked}
                onChange={(e) => togglePage(e.target.checked)}
              />
            </th>
            <th>Email</th>
            <th>First name</th>
            <th>Last name</th>
            <th>Assessments</th>
          </tr>
        </thead>
        <tbody>
          {students.map((s) => (
            <tr key={s.id}>
              <td>
                <Form.Check
                  type="checkbox"
                  aria-label={`Select ${s.email || s.id}`}
                  checked={selected.has(s.id)}
                  onChange={() => toggle(s.id)}
                />
              </td>
              <td>{s.email || s.id}</td>
              <td>{s.first_name}</td>
              <td>{s.last_name}</td>
              <td>{s.assessment_count ?? 0}</td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div
        className="d-flex align-items-center"
        style={{ gap: "12px", marginTop: "4px" }}
      >
        <Button
          variant="outline-secondary"
          size="sm"
          disabled={offset === 0}
          onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
        >
          « Prev
        </Button>
        <span className="text-muted">
          {total === 0 ? "No students" : `Showing ${rangeStart}–${rangeEnd} of ${total}`}
        </span>
        <Button
          variant="outline-secondary"
          size="sm"
          disabled={rangeEnd >= total}
          onClick={() => setOffset(offset + PAGE_SIZE)}
        >
          Next »
        </Button>
      </div>
    </div>
  );
};
